/**
 * `useOrgEntitlements` + `useHasFeature`.
 *
 * Fetches the current org's effective entitlements via
 * `orgEntitlementsAPI.getByOrgId` and exposes both the raw object and
 * the convenience `hasFeature(key)` predicate. Every gated frontend
 * surface (Slack integration, SSO, etc.) consumes one of these.
 *
 * The fetch fires once per (orgId) and caches in module state for
 * the session — entitlements rarely change during a session, and a
 * stale read for a few minutes is acceptable (the next route reload
 * picks up the change). For real-time invalidation, the admin
 * Billing UI's "Edit plan" / "Publish new version" actions can
 * `invalidateOrgEntitlements(orgId)` after a successful save.
 *
 * Returning `null` while loading + an empty feature set on error is
 * the safer default for gating — features render LOCKED until the
 * read confirms they're unlocked, so a transient API failure can't
 * accidentally show a paid surface.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  orgEntitlementsAPI,
  type OrgEntitlementsResponse,
} from '@/lib/api'

/** Per-orgId entitlements cache + the in-flight promise so multiple
 * components mounting at once share one fetch. */
const cache = new Map<string, Promise<OrgEntitlementsResponse>>()

function fetchEntitlements(orgId: string): Promise<OrgEntitlementsResponse> {
  let p = cache.get(orgId)
  if (!p) {
    p = orgEntitlementsAPI.getByOrgId(orgId).catch((err) => {
      // Drop the failed promise from cache so a retry fires a fresh
      // request rather than reusing the rejection forever.
      cache.delete(orgId)
      throw err
    })
    cache.set(orgId, p)
  }
  return p
}

/** Clear the cached entitlements for an org — call after a save that
 * changed the org's plan / override. */
export function invalidateOrgEntitlements(orgId: string): void {
  cache.delete(orgId)
}

export interface UseOrgEntitlementsResult {
  /** Loaded entitlements, or `null` while fetching / on error. */
  entitlements: OrgEntitlementsResponse | null
  /** Lock-state predicate. Returns `false` while loading so gated
   * surfaces render locked until the read confirms entitlement. */
  hasFeature: (key: string) => boolean
  /** True while the initial fetch is in flight (no cached data). */
  loading: boolean
  /** Error string from the initial fetch, if any. The hook still
   * returns `hasFeature: () => false` in this state so the gating
   * stays safe. */
  error: string | null
}

export function useOrgEntitlements(
  orgId: string | undefined,
): UseOrgEntitlementsResult {
  const [data, setData] = useState<OrgEntitlementsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) {
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    // Clear the previous org's entitlements immediately so `hasFeature`
    // returns false for the duration of the in-flight fetch rather than
    // reflecting the PRIOR org. Without this, switching orgs briefly
    // evaluates features against the old org and can flash a gated
    // surface (e.g. the Run Budgets tab) before the new org's read
    // resolves — this honors the documented "false while loading"
    // contract the hook already promises (#1106).
    setData(null)
    setError(null)
    setLoading(true)
    fetchEntitlements(orgId)
      .then((r) => {
        if (cancelled) return
        setData(r)
        setError(null)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to read entitlements')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  const featureSet = useMemo(() => {
    return new Set(data?.features ?? [])
  }, [data])

  const hasFeature = useMemo(() => {
    return (key: string) => featureSet.has(key)
  }, [featureSet])

  return { entitlements: data, hasFeature, loading, error }
}
